import React, { useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Container,
  Snackbar,
  Alert,
} from "@mui/material";
import { motion } from "framer-motion";

const API_URL = process.env.REACT_APP_API_URL;

function NewsletterSection() {
  const [email, setEmail] = useState("");
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [error, setError] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address.");
      setOpenSnackbar(true);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/newsletter_subscribe/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok) {
        setIsSubscribed(true);
        setError("");
      } else {
        setError(
          data.detail === "This email is already subscribed."
            ? data.detail
            : data.detail || "Something went wrong. Please try again."
        );
        setIsSubscribed(false);
      }

      setOpenSnackbar(true);
      setEmail("");
    } catch {
      setIsSubscribed(false);
      setError("There was an error processing your request. Please try again later.");
      setOpenSnackbar(true);
    }
  };

  return (
    <Box
      component="section"
      sx={{
        position: "relative",
        overflow: "hidden",
        background: "#000000",
        py: {
          xs: "clamp(48px, 10vw, 72px)",
          md: "clamp(72px, 8vw, 96px)",
          xl: "clamp(72px, 6vw, 112px)",
        },
      }}
    >
      {/* ── Ambient glow orb ── */}
      <Box
        component={motion.div}
        animate={{
          scale: [1, 1.1, 1],
          opacity: [0.4, 0.8, 0.4],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: "easeInOut",
        }}
        sx={{
          position: "absolute",
          width: {
            xs: "min(220px, 70vw)",
            sm: "min(340px, 60vw)",
            md: "min(520px, 55vw)",
            lg: "min(650px, 50vw)",
            xl: 720,
          },
          height: {
            xs: "min(220px, 70vw)",
            sm: "min(340px, 60vw)",
            md: "min(520px, 55vw)",
            lg: "min(650px, 50vw)",
            xl: 720,
          },
          borderRadius: "50%",
          background: "radial-gradient(circle, rgba(30,144,255,.12), transparent 70%)",
          top: "-10%",
          left: "50%",
          transform: "translateX(-50%)",
          filter: {
            xs: "blur(60px)",
            md: "blur(80px)",
            xl: "blur(90px)",
          },
          pointerEvents: "none",
        }}
      />

      <Container
        maxWidth="lg"
        sx={{
          px: {
            xs: "clamp(14px, 4vw, 20px)",
            sm: "clamp(20px, 4vw, 28px)",
            md: "clamp(24px, 3.5vw, 36px)",
            xl: "clamp(28px, 3vw, 48px)",
          },
        }}
      >
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          sx={{
            position: "relative",
            zIndex: 2,
            textAlign: "center",
            p: {
              xs: "clamp(20px, 6vw, 32px)",
              sm: "clamp(28px, 5vw, 40px)",
              md: "clamp(36px, 4.5vw, 52px)",
              xl: "clamp(44px, 4vw, 64px)",
            },
            borderRadius: {
              xs: "18px",
              sm: "22px",
              md: "26px",
              xl: "30px",
            },
            background: "rgba(8,8,8,.92)",
            border: "1px solid rgba(30,144,255,.18)",
            backdropFilter: "blur(18px)",
            WebkitBackdropFilter: "blur(18px)",
            boxShadow:
              "0 10px 40px rgba(30,144,255,.10), inset 0 1px 0 rgba(255,255,255,.04)",
            overflow: "hidden",
          }}
        >
          {/* Top glow line */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: { xs: "5%", sm: "10%" },
              right: { xs: "5%", sm: "10%" },
              height: "1px",
              background:
                "linear-gradient(to right, transparent, rgba(30,144,255,.6), transparent)",
              pointerEvents: "none",
            }}
          />

          {/* Heading */}
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: "clamp(1.7rem, 5.5vw, 4rem)",
              lineHeight: { xs: 1.05, md: 1.1 },
              letterSpacing: "clamp(-1.5px, -0.04em, -2.5px)",
              mb: { xs: "clamp(12px, 3vw, 20px)", md: "clamp(16px, 2.5vw, 24px)" },
              px: { xs: 0, sm: 0 },
              wordBreak: "break-word",
              overflowWrap: "break-word",
              color: "#FFFFFF",
              fontFamily: "'Bricolage Grotesque', sans-serif",
            }}
          >
            Stay Updated
            <br />
            <Box
              component="span"
              sx={{
                background: "#1E90FF",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              with Us
            </Box>
          </Typography>

          {/* Subtitle */}
          <Typography
            sx={{
              color: "rgba(210,230,255,.72)",
              fontSize: "clamp(0.82rem, 2vw, 1.05rem)",
              lineHeight: { xs: 1.8, md: 1.9 },
              maxWidth: {
                xs: "100%",
                sm: "min(90%, 520px)",
                md: "min(80%, 640px)",
                xl: "min(70%, 680px)",
              },
              mx: "auto",
              mb: {
                xs: "clamp(24px, 6vw, 36px)",
                md: "clamp(32px, 5vw, 44px)",
              },
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            Subscribe to our newsletter for the latest updates, insights, and AI
            innovations delivered directly to your inbox.
          </Typography>

          {/* Form */}
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "column", md: "row" },
              gap: { xs: "clamp(10px, 3vw, 14px)", md: "clamp(12px, 2vw, 16px)" },
              alignItems: { xs: "stretch", md: "flex-start" },
              justifyContent: "center",
              maxWidth: { xs: "100%", md: "660px", lg: "720px", xl: "780px" },
              mx: "auto",
            }}
          >
            <TextField
              label="Enter your email"
              variant="outlined"
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={Boolean(error)}
              helperText={error}
              required
              sx={{
                flex: 1,
                "& .MuiOutlinedInput-root": {
                  borderRadius: { xs: "12px", md: "14px" },
                  background: "rgba(255,255,255,.03)",
                  color: "#FFFFFF",
                  transition: ".3s ease",
                  "& fieldset": {
                    borderColor: "rgba(30,144,255,.18)",
                  },
                  "&:hover fieldset": {
                    borderColor: "rgba(30,144,255,.5)",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#1E90FF",
                  },
                },
                "& .MuiInputLabel-root": {
                  color: "rgba(255,255,255,.55)",
                  fontSize: { xs: "0.88rem", md: "0.95rem" },
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: "#63B8FF",
                },
                "& input": {
                  color: "#FFFFFF",
                  py: { xs: "clamp(11px, 2.5vw, 14px)", md: "clamp(13px, 2vw, 16px)" },
                  fontSize: { xs: "clamp(0.82rem, 2vw, 0.92rem)", md: "1rem" },
                },
                "& .MuiFormHelperText-root": {
                  color:
                    error === "This email is already subscribed."
                      ? "#63B8FF"
                      : "#ff6b6b",
                  ml: 0.5,
                  fontSize: { xs: "0.72rem", md: "0.75rem" },
                },
              }}
            />

            <Button
              type="submit"
              variant="contained"
              size="large"
              sx={{
                px: { xs: "clamp(20px, 5vw, 32px)", md: "clamp(28px, 4vw, 40px)" },
                py: {
                  xs: "clamp(11px, 2.5vw, 14px)",
                  md: "clamp(13px, 1.8vw, 16px)",
                },
                width: { xs: "100%", md: "auto" },
                minWidth: { md: "clamp(160px, 18vw, 220px)" },
                flexShrink: 0,
                borderRadius: { xs: "12px", md: "14px" },
                fontWeight: 700,
                fontSize: {
                  xs: "clamp(0.82rem, 2.5vw, 0.92rem)",
                  md: "clamp(0.88rem, 1.5vw, 0.95rem)",
                },
                textTransform: "none",
                color: "#FFFFFF",
                background: "linear-gradient(90deg,#1E90FF 0%,#42A5FF 100%)",
                boxShadow: "0 8px 30px rgba(30,144,255,.28)",
                transition: ".3s ease",
                whiteSpace: "nowrap",
                alignSelf: { xs: "stretch", md: "flex-start" },
                "&:hover": {
                  background: "linear-gradient(90deg,#42A5FF 0%,#1E90FF 100%)",
                  boxShadow: "0 10px 40px rgba(30,144,255,.42)",
                  transform: "translateY(-2px)",
                },
              }}
            >
              Subscribe Now
            </Button>
          </Box>
        </Box>
      </Container>

      {/* Snackbar */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={5000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        sx={{
          /* Ensure snackbar doesn't overflow on small screens */
          "& .MuiSnackbarContent-root": { maxWidth: "calc(100vw - 32px)" },
          bottom: { xs: "16px !important", md: "24px !important" },
          left: { xs: "50% !important" },
          right: { xs: "auto !important" },
          transform: { xs: "translateX(-50%) !important", md: "translateX(-50%) !important" },
          width: { xs: "calc(100vw - 32px)", sm: "auto" },
          maxWidth: { xs: "calc(100vw - 32px)", sm: "560px" },
        }}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity={
            isSubscribed
              ? "success"
              : error === "This email is already subscribed."
              ? "info"
              : "error"
          }
          sx={{
            width: "100%",
            background: isSubscribed
              ? "#1E90FF"
              : error === "This email is already subscribed."
              ? "#1565C0"
              : "#f44336",
            color: "#FFFFFF",
            borderRadius: "12px",
            fontWeight: 500,
            fontSize: { xs: "0.82rem", md: "0.9rem" },
            alignItems: "center",
            "& .MuiAlert-icon": { color: "#FFFFFF" },
            "& .MuiAlert-action button": { color: "rgba(255,255,255,.8)" },
          }}
        >
          {isSubscribed
            ? "Thank you for subscribing! You'll hear from us soon."
            : error}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default NewsletterSection;